import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { Sheet } from "./Sheet.js";
import { Drawer } from "./Drawer.js";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { useMediaQuery } from "../hooks/index.js";

const Root = ({
  children,
  drawer: { shouldScaleBackground = true, ...props },
  dialog: { ...dialogProps }
}: {
  children: React.ReactNode;
  drawer: React.ComponentProps<typeof DrawerPrimitive.Root>;
  dialog: React.ComponentProps<typeof SheetPrimitive.Root>;
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return (
      <SheetPrimitive.Root {...dialogProps}>{children}</SheetPrimitive.Root>
    );
  }
  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      {...props}
    >
      {children}
    </DrawerPrimitive.Root>
  );
};

Root.displayName = "DrawerOrSheetRoot";

const Trigger = ({ children }: { children: React.ReactNode }) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return <SheetPrimitive.Trigger asChild>{children}</SheetPrimitive.Trigger>;
  }
  return <DrawerPrimitive.Trigger asChild>{children}</DrawerPrimitive.Trigger>;
};
Trigger.displayName = "DrawerOrSheetTrigger";

const Content = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    slideoutContainerId?: string;
  }
>(({ children, slideoutContainerId, ...props }, ref) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return (
      <Sheet.Content
        ref={ref}
        {...props}
        sheetContainerId={slideoutContainerId}
      >
        {children}
      </Sheet.Content>
    );
  }
  return (
    <Drawer.Content
      ref={ref}
      {...props}
      slideoutContainerId={slideoutContainerId}
    >
      {children}
    </Drawer.Content>
  );
});

Content.displayName = "DrawerOrSheetContent";

const Title = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ children, ...props }, ref) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return (
      <Sheet.Title ref={ref} {...props}>
        {children}
      </Sheet.Title>
    );
  }
  return (
    <Drawer.Title ref={ref} {...props}>
      {children}
    </Drawer.Title>
  );
});

Title.displayName = "DrawerOrSheetTitle";

const Header = React.forwardRef<
  HTMLDivElement,
  {
    drawer?: React.HTMLAttributes<HTMLDivElement>;
    children: React.ReactNode;
    sheet?: React.HTMLAttributes<HTMLDivElement>;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ children, sheet, drawer, ...rest }, ref) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return (
      <Sheet.Header ref={ref} {...sheet} {...rest}>
        {children}
      </Sheet.Header>
    );
  }
  return (
    <Drawer.Header ref={ref} {...drawer} {...rest}>
      {children}
    </Drawer.Header>
  );
});

Header.displayName = "DrawerOrSheetHeader";

const DrawerOrSheet = {
  Root,
  Header,
  Trigger,
  Content,
  Title
};

export { DrawerOrSheet };
